import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import DateRangePicker from '../components/common/DateRangePicker'
import LineChart from '../components/charts/LineChart'
import WaterfallChart from '../components/charts/WaterfallChart'
import DoughnutChart from '../components/charts/DoughnutChart'
import BarChart from '../components/charts/BarChart'
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatPercent,
  calculateGrowth,
  getCountryName
} from '../utils/formatters'

export default function RevenueOverview() {
  const { data, computed, loading, error } = useData()
  const [startMonth, setStartMonth] = useState(null)
  const [endMonth, setEndMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months, arrByMonth } = computed
    const start = startMonth || months[Math.max(0, months.length - 12)]
    const end = endMonth || months[months.length - 1]

    const filteredMonths = months.filter(m => m >= start && m <= end)

    // ARR trend data
    const arrTrend = filteredMonths.map(month => arrByMonth[month] || 0)

    // Movements for waterfall
    const latestMonth = filteredMonths[filteredMonths.length - 1]
    const previousMonth = filteredMonths[filteredMonths.length - 2]

    const movementsThisMonth = data.arrMovements.filter(m => m.month === latestMonth)
    const movementsByType = movementsThisMonth.reduce((acc, m) => {
      acc[m.movement_type] = (acc[m.movement_type] || 0) + m.arr_change
      return acc
    }, {})

    const startingArr = arrByMonth[previousMonth] || 0
    const endingArr = arrByMonth[latestMonth] || 0

    const waterfallData = [
      { label: 'Starting ARR', value: startingArr },
      { label: 'New Business', value: movementsByType['New Business'] || 0 },
      { label: 'Expansion Upsell', value: movementsByType['Expansion Upsell'] || 0 },
      { label: 'Expansion Resell', value: movementsByType['Expansion Resell'] || 0 },
      { label: 'Contraction Downsell', value: movementsByType['Contraction Downsell'] || 0 },
      { label: 'Contraction Resell', value: movementsByType['Contraction Resell'] || 0 },
      { label: 'Churn', value: movementsByType['Churn'] || 0 },
      { label: 'Ending ARR', value: endingArr }
    ]

    // ARR by country for latest month
    const arrByCountry = data.monthlyArr
      .filter(r => r.month === latestMonth)
      .reduce((acc, r) => {
        acc[r.country] = (acc[r.country] || 0) + r.arr_value
        return acc
      }, {})

    // ARR by subscription for latest month
    const arrBySubscription = data.monthlyArr
      .filter(r => r.month === latestMonth)
      .reduce((acc, r) => {
        acc[r.subscription_type] = (acc[r.subscription_type] || 0) + r.arr_value
        return acc
      }, {})

    // Country table data
    const countryTableData = Object.entries(arrByCountry)
      .map(([country, arr]) => {
        const countryMovements = movementsThisMonth.filter(m => m.country === country)
        const added = countryMovements
          .filter(m => m.arr_change > 0)
          .reduce((sum, m) => sum + m.arr_change, 0)
        const lost = Math.abs(countryMovements
          .filter(m => m.arr_change < 0)
          .reduce((sum, m) => sum + m.arr_change, 0))

        return {
          country,
          countryName: getCountryName(country),
          currentArr: arr,
          netNewArr: added - lost,
          addedArr: added,
          lostArr: lost,
          growthRate: arr > 0 ? (added - lost) / arr : 0
        }
      })
      .sort((a, b) => b.currentArr - a.currentArr)

    // Subscription table data
    const subscriptionTableData = Object.entries(arrBySubscription)
      .map(([type, arr]) => {
        const typeMovements = movementsThisMonth.filter(m => m.subscription_type === type)
        const added = typeMovements
          .filter(m => m.arr_change > 0)
          .reduce((sum, m) => sum + m.arr_change, 0)
        const lost = Math.abs(typeMovements
          .filter(m => m.arr_change < 0)
          .reduce((sum, m) => sum + m.arr_change, 0))

        return {
          subscriptionType: type,
          currentArr: arr,
          netNewArr: added - lost,
          addedArr: added,
          lostArr: lost,
          growthRate: arr > 0 ? (added - lost) / arr : 0
        }
      })
      .sort((a, b) => b.currentArr - a.currentArr)

    return {
      months: filteredMonths,
      arrTrend,
      waterfallData,
      arrByCountry,
      arrBySubscription,
      countryTableData,
      subscriptionTableData,
      currentArr: endingArr,
      previousArr: startingArr
    }
  }, [data, computed, startMonth, endMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const {
    months,
    arrTrend,
    waterfallData,
    arrByCountry,
    arrBySubscription,
    countryTableData,
    subscriptionTableData,
    currentArr,
    previousArr
  } = filteredData

  const growth = calculateGrowth(currentArr, previousArr)
  const netNewArr = currentArr - previousArr

  const totalAdded = waterfallData
    .filter(d => d.value > 0 && d.label !== 'Starting ARR' && d.label !== 'Ending ARR')
    .reduce((sum, d) => sum + d.value, 0)

  const totalLost = Math.abs(waterfallData
    .filter(d => d.value < 0)
    .reduce((sum, d) => sum + d.value, 0))

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing data for {months.length} months
        </div>
        <DateRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          months={computed.months}
          onStartChange={setStartMonth}
          onEndChange={setEndMonth}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Current ARR"
          value={formatCurrency(currentArr)}
          trend={growth !== null ? {
            direction: growth >= 0 ? 'up' : 'down',
            value: formatPercent(Math.abs(growth)),
            label: 'vs previous month'
          } : null}
          icon={CurrencyDollarIcon}
          colorScheme="default"
        />
        <KPICard
          title="Total Added ARR"
          value={formatCurrency(totalAdded)}
          icon={ArrowTrendingUpIcon}
          colorScheme="success"
        />
        <KPICard
          title="Total Lost ARR"
          value={formatCurrency(totalLost)}
          icon={ChartBarIcon}
          colorScheme="danger"
        />
        <KPICard
          title="Net New ARR"
          value={formatCurrency(netNewArr)}
          trend={{
            direction: netNewArr >= 0 ? 'up' : 'down',
            value: formatCurrency(Math.abs(netNewArr)),
            label: ''
          }}
          icon={ArrowTrendingUpIcon}
          colorScheme={netNewArr >= 0 ? 'success' : 'danger'}
        />
        <KPICard
          title="Active Customers"
          value={computed.activeCustomers.length.toLocaleString()}
          icon={UserGroupIcon}
          colorScheme="default"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Annual Recurring Revenue" subtitle="Monthly ARR trend" height="h-72">
          <LineChart
            labels={months}
            datasets={[{
              label: 'ARR',
              data: arrTrend,
              color: '#3b82f6'
            }]}
            fill={true}
          />
        </ChartCard>

        <ChartCard title="ARR Movement" subtitle="Changes this month" height="h-72">
          <WaterfallChart data={waterfallData} />
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="ARR by Country" height="h-72">
          <DoughnutChart
            labels={Object.keys(arrByCountry).map(getCountryName)}
            data={Object.values(arrByCountry)}
          />
        </ChartCard>

        <ChartCard title="ARR by Subscription Type" height="h-72">
          <BarChart
            labels={Object.keys(arrBySubscription)}
            datasets={[{
              label: 'ARR',
              data: Object.values(arrBySubscription),
              color: '#6366f1'
            }]}
            horizontal={true}
            formatLabels={false}
            aspectRatio={1.5}
          />
        </ChartCard>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Overview per Country" height="h-auto">
          <DataTable
            columns={[
              { key: 'countryName', header: 'Country' },
              { key: 'currentArr', header: 'Current ARR', render: v => formatCurrency(v) },
              { key: 'netNewArr', header: 'Net New ARR', render: v => (
                <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {formatCurrency(v)}
                </span>
              )},
              { key: 'addedArr', header: 'Added', render: v => formatCurrency(v) },
              { key: 'lostArr', header: 'Lost', render: v => formatCurrency(v) },
              { key: 'growthRate', header: 'Growth %', render: v => formatPercent(v) }
            ]}
            data={countryTableData}
            pageSize={6}
          />
        </ChartCard>

        <ChartCard title="Overview per Subscription Type" height="h-auto">
          <DataTable
            columns={[
              { key: 'subscriptionType', header: 'Type' },
              { key: 'currentArr', header: 'Current ARR', render: v => formatCurrency(v) },
              { key: 'netNewArr', header: 'Net New ARR', render: v => (
                <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {formatCurrency(v)}
                </span>
              )},
              { key: 'addedArr', header: 'Added', render: v => formatCurrency(v) },
              { key: 'lostArr', header: 'Lost', render: v => formatCurrency(v) },
              { key: 'growthRate', header: 'Growth %', render: v => formatPercent(v) }
            ]}
            data={subscriptionTableData}
            pageSize={6}
          />
        </ChartCard>
      </div>
    </div>
  )
}
